import { Button } from '@/components/ui/button';
import { DocumentViewer } from '@/components/ui/document-viewer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useDeleteDocument,
  useListDocuments,
  useUploadDocument,
} from '@/hooks/useDocuments';
import { AssetService } from '@/services/AssetService';
import type { Asset } from '@/types/asset';
import { FileText, MoreHorizontal, Plus } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const DocumentsPage: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadTags, setUploadTags] = useState<string>('');
  const [tagFilter, setTagFilter] = useState<string>('');
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [viewDoc, setViewDoc] = useState<any | null>(null);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const {
    data: documents,
    isLoading,
    error,
  } = useListDocuments({ tags: tagFilter });
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();

  useEffect(() => {
    setAssetsLoading(true);
    AssetService.getAssets()
      .then(setAssets)
      .catch(() => setAssets([]))
      .finally(() => setAssetsLoading(false));
  }, []);

  // Map asset_id to asset for fast lookup
  const assetMap = useMemo(() => {
    const map: Record<string, Asset> = {};
    for (const a of assets) {
      map[a.id] = a;
    }
    return map;
  }, [assets]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    if (uploadTags.trim()) {
      formData.append('tags', uploadTags);
    }
    uploadMutation.mutate(formData, {
      onSuccess: () => {
        setShowUpload(false);
        setUploadTags('');
      },
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Delete this document?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleView = (doc: any) => {
    setViewDoc(doc);
  };

  const handleCloseView = () => {
    setViewDoc(null);
  };

  const renderTags = (tags?: string[]) => {
    if (!tags || tags.length === 0) return null;
    return (
      <span className="flex flex-wrap gap-1">
        {tags.map((tag, idx) => (
          <span
            key={idx}
            className="bg-gray-200 text-xs px-2 py-0.5 rounded-full text-gray-700"
          >
            {tag}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="w-full px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">All Documents</h1>
        <Button onClick={() => setShowUpload((v) => !v)}>
          {showUpload ? 'Cancel' : 'Add Document'}
        </Button>
      </div>
      {showUpload && (
        <div className="mb-6 border rounded p-4 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              id="all-doc-upload"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              Choose File
            </Button>
            <input
              type="text"
              placeholder="Add tags (comma separated)"
              value={uploadTags}
              onChange={(e) => setUploadTags(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
              style={{ minWidth: 180 }}
            />
            {uploadMutation.isPending && <span>Uploading...</span>}
          </div>
        </div>
      )}
      <div className="mb-4 flex items-center gap-2">
        <input
          type="text"
          placeholder="Filter by tag(s)"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
          style={{ minWidth: 180 }}
        />
        <Button size="sm" variant="outline" onClick={() => setTagFilter('')}>
          Clear
        </Button>
      </div>
      {(isLoading || assetsLoading) && <div>Loading documents...</div>}
      {error && <div className="text-red-500">Error loading documents</div>}

      {documents && documents.length > 0 ? (
        <table className="w-full text-sm border rounded">
          <thead>
            <tr>
              <th className="text-left py-2 px-2">Name</th>
              <th className="text-left py-2 px-2">Asset</th>
              <th className="text-left py-2 px-2">Tags</th>
              <th className="text-left py-2 px-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((doc: any) => (
              <tr key={doc.id} className="border-b last:border-0">
                <td className="py-2 px-2 truncate max-w-xs">
                  {doc.original_name}
                </td>
                <td className="py-2 px-2">
                  {doc.asset_id && assetMap[doc.asset_id] ? (
                    <Link
                      to={`/app/assets/${assetMap[doc.asset_id].slug}`}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      {assetMap[doc.asset_id].name}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="py-2 px-2">{renderTags(doc.tags)}</td>
                <td className="py-2 px-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="link"
                        className="p-0 h-auto text-blue-600"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem onClick={() => handleView(doc)}>
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDelete(doc.id)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="text-center py-12 border rounded-lg bg-muted/50">
          <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-medium mb-2">No Documents</h3>
          <p className="text-muted-foreground mb-4">
            Upload your first document to start organizing your files
          </p>
          <Button onClick={() => setShowUpload(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Document
          </Button>
        </div>
      )}

      {/* Document Viewer */}
      <DocumentViewer
        document={viewDoc}
        isOpen={!!viewDoc}
        onClose={handleCloseView}
      />
    </div>
  );
};

export default DocumentsPage;
